import Head from "next/head";

import { useRouter } from "next/router";

import { useTranslator } from "@eo-locale/react";

import { useEffect } from "react";

import { Box, HTMLWrapper, Stack } from "@/storybook-components";
import SurveyForm from "./form";
import SurveyResponse from "./response";
import { stripAllHtml } from "@/lib";

import type { SurveyParams } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import UnverifiedUserForm from "../unverified-user/form";
import Alerts from "../alerts";
import useUser from "@/hooks/useUser";

import SurveyHeader from "./header";
import useEmbeds from "@/hooks/useEmbeds";
import usePinned from "@/hooks/usePinned";
import { fetchProject } from "@/redux/features/project/thunk";

const App = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { isEmbed, isInsideIframe, isInsideIframeAndOnCurrentUrl } =
    useEmbeds();
  const { query, asPath } = useRouter();
  const { translate } = useTranslator();
  const { site } = useAppSelector((state) => state.site);
  const { disableSignup } = useAppSelector((state) => state.settings?.settings);
  const { stylesheetSrc, projectId, surveyId } = query as Partial<SurveyParams>;
  const {
    visibilityMode,
    canContribute,
    id: projectName,
    state: projectState
  } = useAppSelector((state) => state.project.project);
  const { survey, fetchError, viewResponse, taken } = useAppSelector(
    (state) => state.survey
  );
  const {
    isRegisteredUserLoggedIn,
    isUnverfiedUserLoggedIn,
    isUserAdmin,
    isPreviewUser
  } = useUser();
  const { isPinned } = usePinned();

  const {
    name,
    state: surveyState,
    description,
    archivalReasonMessage,
    allowAnonymousParticipation,
    allowUnverifiedParticipation,
    allowUserToPostMultipleTimes,
    multipleTimesMsg
  } = survey;

  const allowOnlyRegisteredParticipation =
    !allowUnverifiedParticipation && !allowAnonymousParticipation;

  const getRoute = (route: "login" | "register") => {
    if (isPinned)
      return `/${route}?next=${encodeURIComponent(`/${projectId}?tool=survey_tool&tool_id=${surveyId}#tool_tab`)}`;

    if (isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl)
      return `/${route}`;

    return `/${route}?next=${encodeURIComponent(asPath)}`;
  };

  const getRouteTarget = () =>
    isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl
      ? "_blank"
      : "_parent";

  useEffect(() => {
    const getProject = async () => await dispatch(fetchProject({ projectId }));

    if (!projectName) getProject();
  }, [projectName, dispatch, projectId]);

  const surveyTakerNode = () => {
    return (
      <Stack spacing={12}>
        <SurveyHeader />
        {viewResponse ? <SurveyResponse /> : <SurveyForm />}
      </Stack>
    );
  };

  const surveyTakenNode = () => {
    return (
      <Stack spacing={12}>
        <SurveyHeader />
        <Alerts
          status="info"
          title={translate("survey.alreadyTakenPartInSurvey")}
          description={
            multipleTimesMsg || translate("survey.thanksForParticipating")
          }
        />
      </Stack>
    );
  };

  const loginToTakeTheSurveyNode = () => {
    return (
      <Stack spacing={12}>
        <SurveyHeader />
        {!disableSignup && (
          <Alerts
            status="info"
            title={translate("survey.createAccountForSurvey")}
            description={
              <HTMLWrapper
                html={translate("common.signInOrRegister", {
                  loginRoute: getRoute("login"),
                  registerRoute: getRoute("register"),
                  target: getRouteTarget()
                })}
              />
            }
          />
        )}
        <Box>
          <HTMLWrapper
            html={translate("common.refreshIfSignIn", {
              refreshRoute: asPath,
              target: "_self"
            })}
          />
        </Box>
      </Stack>
    );
  };

  const unverifiedUserLoginNode = () => {
    return (
      <Stack spacing={12}>
        <SurveyHeader />
        <UnverifiedUserForm tool={translate("survey.survey")} />
      </Stack>
    );
  };

  const visibilityPanelledNode = () => {
    if (!isRegisteredUserLoggedIn) {
      return (
        <>
          <Alerts
            status="info"
            title={translate("common.paneledProjectNotLoggedIn")}
            description={translate("common.refreshOnSignIn")}
          />
          <Box mt={6}>
            <HTMLWrapper
              html={
                disableSignup
                  ? translate("common.pleaseSignIn", {
                      loginRoute: getRoute("login"),
                      target: getRouteTarget()
                    })
                  : translate("common.loginOrRegister", {
                      loginRoute: getRoute("login"),
                      registerRoute: getRoute("register"),
                      target: getRouteTarget()
                    })
              }
            />
          </Box>
          <br />
          {surveyTakerNode()}
        </>
      );
    } else if (!canContribute) {
      return (
        <>
          <Alerts
            status="info"
            title={translate("common.paneledProjectNotLoggedIn")}
          />
          <br />
          {surveyTakerNode()}
        </>
      );
    }

    if (
      allowUserToPostMultipleTimes &&
      !allowAnonymousParticipation &&
      !allowUnverifiedParticipation &&
      taken
    ) {
      return surveyTakerNode();
    }

    if (allowOnlyRegisteredParticipation) {
      if (!isRegisteredUserLoggedIn) {
        return loginToTakeTheSurveyNode();
      } else if (!isUserAdmin && taken) {
        return surveyTakenNode();
      }
    }

    return surveyTakerNode();
  };

  const surveyNode = () => {
    if (projectState === "archived" || surveyState === "archived") {
      return (
        <>
          <Alerts
            status="warning"
            title={archivalReasonMessage || translate("survey.archivedError")}
          />
          <br />
          {isUserAdmin ? (
            surveyTakerNode()
          ) : (
            <Stack spacing={12}>
              <SurveyHeader />
            </Stack>
          )}
        </>
      );
    }

    if (
      ((projectState === "draft" && !isPreviewUser) ||
        surveyState === "draft") &&
      !isUserAdmin
    ) {
      return <Alerts status="info" title={translate("survey.draftError")} />;
    }

    if (visibilityMode === "Panelled") {
      return visibilityPanelledNode();
    }

    if (
      allowUserToPostMultipleTimes &&
      !allowAnonymousParticipation &&
      !allowUnverifiedParticipation &&
      taken
    ) {
      return surveyTakerNode();
    }

    if (allowOnlyRegisteredParticipation) {
      if (!isRegisteredUserLoggedIn) {
        return loginToTakeTheSurveyNode();
      } else if (!isUserAdmin && taken) {
        return surveyTakenNode();
      }
    }

    if (
      allowUnverifiedParticipation &&
      !isRegisteredUserLoggedIn &&
      !isUnverfiedUserLoggedIn &&
      // embeds doesn't fetch project data, so bypassing this check
      (isEmbed ? true : projectState === "published") &&
      surveyState === "published"
    ) {
      return unverifiedUserLoginNode();
    }

    return surveyTakerNode();
  };

  const errorNode = () => {
    return <Alerts status="error" title={translate("survey.fetchError")} />;
  };

  return (
    <>
      <Head>
        <title>{`${name || translate("survey.survey")} | ${site.name}`}</title>
        {stylesheetSrc && <link href={stylesheetSrc} rel="stylesheet" />}
        {description && (
          <meta name="description" content={stripAllHtml(description)} />
        )}
      </Head>

      <Box className="ehq-surveyContainer">
        {fetchError ? errorNode() : surveyNode()}
      </Box>
    </>
  );
};

export default App;
